import type { TableColumnType, TableRowType } from '../components/editor/grid/reduxStore/table.types';
import { RowTypesIdentifier } from '../components/editor/grid/reduxStore/table.types';
import { DATA_STYLE_SET, DATA_TABLE_STYLE } from '../components/editor/grid/reduxStore/tableDefaultStyleValues.ts';
import { isNumericColumn } from '../components/editor/GridDndEditor/Block/Table/helper.ts';

function getEmptyParagraph(newType: string) {
  return `<p data-table-style="table_${newType}_row"></p>`;
}
function isJsonString(str: string): boolean {
  try {
    const parsed = JSON.parse(str);
    return typeof parsed === 'object' && parsed !== null;
  } catch {
    return false;
  }
}

function updateJsonString(str: string, newValue: string) {
  const doc = !str ? getEmptyParagraph(newValue) : JSON.parse(str);

  function update(node) {
    if (node && typeof node === 'object') {
      if (node.attrs && node.attrs['data-style-set'] === null) {
        node.attrs['data-table-style'] = `table_${newValue}_row`;
      }

      if (Array.isArray(node.content)) {
        node.content.forEach(update);
      }
    }
  }

  update(doc);
  return JSON.stringify(doc);
}

function replaceTableRowTypeDOM(htmlString: string, newType: string): string {
  if ((isJsonString(htmlString) && htmlString) || htmlString === '') {
    return updateJsonString(htmlString, newType);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const paragraphs = doc.querySelectorAll(`p[${DATA_TABLE_STYLE}]`);

  if (!paragraphs.length) {
    return getEmptyParagraph(newType);
  }
  paragraphs.forEach((p) => {
    const tableSet = p.getAttribute(DATA_STYLE_SET);
    const styleAttr = p.getAttribute(DATA_TABLE_STYLE);

    if (!tableSet && styleAttr && /^table_.+_row$/.test(styleAttr)) {
      p.setAttribute(DATA_TABLE_STYLE, `table_${newType}_row`);
    }
  });
  return doc.body.innerHTML;
}

export const getSanitizedDataForTableFooter = (
  row: TableRowType,
  columns: TableColumnType[],
  value?: string,
  selectedRowType?: string
): TableRowType => {
  const convertedRowToFooterData: TableRowType = { ...row };
  const indexForInitialColumns = 1;
  const indexForAddedColumns = 2;
  const isInitialStateRow = row.field === '';
  const firstColumnAfterId = isInitialStateRow ? indexForAddedColumns : indexForInitialColumns;

  if (isInitialStateRow) {
    delete convertedRowToFooterData['editable'];
    delete convertedRowToFooterData['width'];
    delete convertedRowToFooterData['headerName'];
    delete convertedRowToFooterData['field'];
    columns.slice(firstColumnAfterId).forEach((column) => {
      convertedRowToFooterData[column.field] = '';
    });
  } else {
    Object.keys(row).forEach((key) => {
      if (!['id', 'rowType', '__reorder__', 'isOptionalToggleAvailable'].includes(key)) {
        const rowValue = row[key];
        const newDataAttributeValue = selectedRowType?.toLowerCase();
        const columnKey = columns.filter((col) => col.field === key && isNumericColumn(col.columnType));
        if (columnKey.length > 0 && selectedRowType !== RowTypesIdentifier.HEADER) {
          convertedRowToFooterData[key] = newDataAttributeValue && getEmptyParagraph(newDataAttributeValue);
        } else {
          const isAvailableForDataAttributeUpdate = newDataAttributeValue && selectedRowType;
          const updatedDataAttributeWithValue = isAvailableForDataAttributeUpdate
            ? replaceTableRowTypeDOM(rowValue, newDataAttributeValue)
            : rowValue;
          convertedRowToFooterData[key] = value ? value : updatedDataAttributeWithValue;
        }
      }
    });
  }
  return convertedRowToFooterData;
};
